import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';

import {
  COMPLEX_FILTER_SERVICE,
  ComplexFilterComponent,
  ComplexFiltersModule,
  LogicalQueryFormGroupValue,
} from '@blaze-runtime/engine-ui-feature';
import {
  ComparisonQueryNode,
  LogicalQueryNode,
  QueryNodeType,
} from '@blaze-shared/api-contracts-domain';
import { BlazeTable } from '@blaze-shared/datasets-domain';
import { DialogModule, SharedModule } from '@blaze-shared/shared-feature';
import { ButtonComponent } from '@blaze-shared/shared-ui';

import { BlazeTableFilterService } from './blaze-table-filter.service';

export interface BlazeTableFilterDialogInput {
  blazeTableConfig$: Observable<BlazeTable>;
  initialFilter: LogicalQueryNode | ComparisonQueryNode;
  setFilterOutput: (filter: LogicalQueryNode | undefined) => void;
}

@UntilDestroy()
@Component({
  selector: 'blaze-table-filter-dialog',
  templateUrl: './blaze-table-filter-dialog.component.html',
  styleUrls: ['./blaze-table-filter-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    SharedModule,
    ButtonComponent,
    CommonModule,
    DialogModule,
    ComplexFiltersModule,
    ComplexFilterComponent,
  ],
  providers: [
    BlazeTableFilterService,
    {
      provide: COMPLEX_FILTER_SERVICE,
      useExisting: BlazeTableFilterService,
    },
  ],
})
export class BlazeTableFilterDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef);
  private readonly blazeTableFilterService = inject(BlazeTableFilterService);
  readonly dialogData: BlazeTableFilterDialogInput = inject(MAT_DIALOG_DATA);

  filter = signal<LogicalQueryNode | undefined>(undefined);

  ngOnInit(): void {
    this.dialogData.blazeTableConfig$.pipe(untilDestroyed(this)).subscribe((res) => {
      this.blazeTableFilterService.setup(res);
    });
  }

  protected queryChange(node: { query: LogicalQueryFormGroupValue }): void {
    const root: LogicalQueryNode = {
      nodeType: QueryNodeType.Logical,
      children: [...node.query.children],
      logicalType: node.query.logicalType,
    };

    this.filter.set(root);
  }

  protected saveFilter(): void {
    this.dialogData.setFilterOutput(this.filter());
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }
}
